import { PageContexts } from '@/addons/enums'
import { FeLogger } from '@/addons/monitoring'
import store from '@/store'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import router from '.'
import { Routes } from './routes'
import modalController, { ModalTypes } from '@/components/modal/ModalController'
import I18n from '@/addons/i18n'
import { ConfigGetters } from '@/store/configs-store'

// API:
// Flag per attivare/disattivare il mock
const USE_MOCK_CASH_CLOSURE_API =
  import.meta.env.VITE_CASH_CLOSURE_MOCK_API === 'true' || false

import { getPoswebOrigin, extractErrorMessage } from '@/addons/functions'
import { getAxiosConfigs } from '@/addons/axios'
import { DefaultApiFactory, InlineResponse2003 } from '@/api/api'
import { NotificationsActions } from '@/store/notifications-store'

/**
 * Checks the cash closure status
 * @returns The cash closure status data or null if an error occurs
 */
async function checkCashClosureStatus(): Promise<InlineResponse2003 | null> {
  // Always throw an error for testing
  // eslint-disable-next-line no-constant-condition
  // if (true) {
  //   throw new Error('Test error for modal')
  // }

  // Se il mock è attivo
  if (USE_MOCK_CASH_CLOSURE_API) {
    return {
      chiusura_corretta: 0, // <-- per abilitare o meno la chiusura,  0 || 1
      chiusura_possibile: 1,
      data_ora_ultima_chiusura: '2025-05-13 13:40:19',
      status: 'CLOSED',
    }
  }

  const basePath = await getPoswebOrigin()
  const configuration = getAxiosConfigs()
  const apiInstance = DefaultApiFactory(configuration, basePath)

  try {
    const response =
      await apiInstance.apiV1AbstridCashClosureStatusCheckClosureGet()

    // console.log('Response:', JSON.stringify(response))

    return response.data || null
  } catch (error: unknown) {
    const err = error as any
    let errorMessage = ''

    if (err?.response?.data?.errors?.length) {
      const details = extractErrorMessage(
        err?.response?.data?.errors[0]?.detail ??
          I18n.global.t('pos_common.generic_error'),
      )
      if (details) {
        errorMessage += `:${details}\n`
      }
    }
    await store.dispatch(NotificationsActions.NOTIFY_ERROR, errorMessage)
    return null
  }
}

/**
 * Navigation guard for cash closure in French stores
 * Checks if the current route is in a FRONTOFFICE context and for a French store
 * Validates cash closure status before allowing navigation
 *
 * @param to Destination route
 * @param from Source route
 * @param next Navigation guard next function
 * @returns Allows or blocks navigation based on cash closure status
 */
export async function cashClosureGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) {
  FeLogger.info(
    `[GuardFrontofficeFR] Cash closure in France store for route ${to.path}, from route ${from.path}.`,
  )
  // Early return if not a FRONTOFFICE context | PAGE url
  if (to.meta.context !== PageContexts.FRONTOFFICE) {
    FeLogger.info(
      '[GuardFrontofficeFR] Not a FRONTOFFICE context, allowing navigation.',
    )
    return next()
  }

  // Early return if not a French store
  if (!isFranceCountry()) {
    FeLogger.info(
      '[GuardFrontofficeFR] Not a French store, allowing navigation.',
    )
    return next()
  }

  try {
    FeLogger.info('[GuardFrontofficeFR] Checking cash closure status...')

    // Get cash closure status
    const status = await checkCashClosureStatus()

    // If status is null or chiusura_corretta is 0, show error modal
    if (!status || status.chiusura_corretta === 0) {
      FeLogger.warn(
        `[GuardFrontofficeFR] Cash closure not correct. Showing error modal for route ${to.path}.`,
      )
      // modale di chiusura cassa
      return showErrorModal(
        I18n.global.t('pos_common.attention'),
        formatModalMessage(
          I18n.global.t('pos_sale.cash_closure_required_title'),
          I18n.global.t('pos_sale.cash_closure_required_text'),
        ),
        I18n.global.t('pos_sale.cash_closure_fix'),
        next,
        () => router.push(Routes.CASH_CLOSING),
      )
    }
  } catch (error) {
    FeLogger.error(
      '[GuardFrontofficeFR] Error while checking cash closure status',
      error,
    )

    // In caso di errore api, blocchiamo l'accesso e mostriamo una modale di errore!
    return showErrorModal(
      I18n.global.t('pos_common.error'),
      I18n.global.t('pos_common.unhandled_error'),
      I18n.global.t('pos_common.back'),
      next,
    )
  }

  // Continue navigation
  return next()
}

/**
 * Checks if the current store is a French store
 * @returns boolean indicating if the current store is French
 */
function isFranceCountry(): boolean {
  const countryCode = store.getters[ConfigGetters.GET_COUNTRY_CODE]
  return countryCode === 'FR'
}

/**
 * Shows an error modal and returns a promise that resolves to false (to block navigation)
 * @param title Modal title
 * @param message Modal message
 * @param confirmLabel Label for confirm button
 * @param confirmAction Optional action to execute on confirm
 * @param next The navigation guard next function
 */
function showErrorModal(
  title: string,
  message: string,
  confirmLabel: string,
  next: NavigationGuardNext,
  confirmAction?: () => void,
) {
  // Blocca immediatamente la navigazione
  next(false)

  modalController.open({
    type: ModalTypes.ERROR,
    title,
    message,
    textAlign: 'center',
    buttonAlign: 'center',
    confirmLabel,
    confirmActionButton: true,
    noCancelButton: true,
    confirmAction: () => {
      if (confirmAction) {
        confirmAction()
      }
    },
  })
}

/**
 * Formats a message with a title for display in fr modal
 * @param title The title to display as h5
 * @param message The message to display as paragraph
 * @returns Formatted HTML string
 */
function formatModalMessage(title: string, message: string): string {
  return `<h5 class="modal-title-h5">${title}</h5><p class="modal-message-p">${message}</p>`
}
